import { ExternalCustomer } from "src/app/backend/models/customer/customer";
import { Pet } from "src/app/backend/models/pet/pet";
import { getCustomerNominative } from "src/app/support/customer";
import { dateUtil } from "src/app/support/date-util";
import { sntTranslate } from "src/app/support/translate";
import { marker } from "@jsverse/transloco-keys-manager/marker";
import { compact, startCase, toLower, trim } from "lodash-es";
import { HealthRecord } from "src/app/backend/models/health-record";
import { getLastWeight } from "../../../support/patient";

export interface SoapDesign {
  reportLine: string;
  customerLine: string;
  patientLine: string;
  staffLines: string[];
  customerNominative: string;
  customerPhone: string;
  customerEmail: string;
  patientName: string;
  patientBreed: string;
  patientSpecies: string;
  patientBirthday: string;
  patientMicrochip: string;
  patientSex: string;
  referringPhysician: string;
  patientSterilized: string;
}

export type SoapDesignKeyConfigItem = { title: string };

export type SoapDesignKeyConfigReturnType = Record<
  string,
  SoapDesignKeyConfigItem
>;

export function soapDesignKeyConfig(): SoapDesignKeyConfigReturnType {
  return {
    customerNominative: {
      title: sntTranslate(marker("Cliente"))
    },
    customerPhone: {
      title: sntTranslate(marker("Telefono"))
    },
    customerEmail: {
      title: sntTranslate(marker("Email"))
    },
    patientName: {
      title: sntTranslate(marker("Nome animale"))
    },
    patientBreed: {
      title: sntTranslate(marker("Razza"))
    },
    patientSpecies: {
      title: sntTranslate(marker("Specie"))
    },
    patientBirthday: {
      title: sntTranslate(marker("Data di nascita"))
    },
    patientSex: {
      title: sntTranslate(marker("Sesso"))
    },
    patientMicrochip: {
      title: sntTranslate(marker("Microchip"))
    }
  };
}

export function buildSoapDesignModel(
  healthRecord:
    | {
        name?: string;
        number: string;
        createdAt?: string;
        completedAt?: string;
        performedAt?: string;
      }
    | undefined,
  customer:
    | (ExternalCustomer & {
        phone?: string;
        email?: string;
        vatCode?: string;
        ssn?: string;
      })
    | undefined,
  patient: Partial<Pet> | undefined
): SoapDesign {
  let reportLine = healthRecord?.name ? healthRecord!.name : "";

  if (healthRecord?.number) {
    reportLine = `${reportLine || ""}${reportLine ? " • " : ""}#${
      healthRecord.number
    }`;
  }

  if (healthRecord?.performedAt) {
    reportLine = trim(
      `${reportLine} ${reportLine ? "•" : ""} ${sntTranslate(
        marker("creato il createdAt"),
        {
          createdAt: dateUtil(healthRecord.performedAt).format("DD/MM/YYYY")
        }
      )}`
    );
  }

  if (healthRecord?.completedAt) {
    reportLine = `${reportLine} • ${sntTranslate(
      marker("completato il completedAt"),
      {
        completedAt: dateUtil(healthRecord.completedAt).format("DD/MM/YYYY")
      }
    )}`;
  }

  const customerNominative = trim(`${getCustomerNominative(customer)}`);

  let customerLine = customerNominative;

  if (customer?.phone && customer?.email) {
    customerLine = `${customerLine} • ${customer.phone} - ${customer.email}`;
  } else if (customer?.phone) {
    customerLine = `${customerLine} • ${customer.phone}`;
  } else if (customer?.email) {
    customerLine = `${customerLine} • ${customer.email}`;
  }

  if (customer?.ssn) {
    customerLine = `${customerLine} • ${customer.ssn}`;
  }

  if (customer?.vatCode) {
    customerLine = `${customerLine} • ${customer.vatCode}`;
  }

  let patientLine = trim(`${patient?.name || ""}`);

  const sex = patient?.sex
    ? patient.sex === "male"
      ? sntTranslate(marker("Maschio"))
      : sntTranslate(marker("Femmina"))
    : null;

  if (patient?.sex || patient?.species || patient?.breed) {
    const species = patient?.species
      ? `${startCase(toLower(sntTranslate(patient?.species || "")))}`
      : null;

    const breed = patient?.breed
      ? startCase(sntTranslate(toLower(patient?.breed || "")))
      : null;

    patientLine = `${patientLine} (${compact([species, breed, sex]).join(" - ")})`;
  }

  if (patient?.birthday) {
    patientLine = `${patientLine} • ${sntTranslate(
      patient?.sex === "male"
        ? marker("nato il birthday")
        : marker("nata il birthday"),
      {
        birthday: dateUtil(patient.birthday).format("DD/MM/YYYY")
      }
    )}`;
  }

  if (patient?.microchip) {
    patientLine = `${patientLine} • #${patient.microchip}`;
  }

  if (patient?.weighing) {
    patientLine = `${patientLine} • ${getLastWeight(patient)} kg`;
  }

  let patientSterilized: boolean = !!patient?.sterilized;

  return {
    reportLine,
    customerLine,
    patientLine,
    staffLines: [],
    customerNominative,
    customerPhone: customer?.phone || "",
    customerEmail: customer?.email || "",
    patientName: trim(`${patient?.name || ""}`),
    patientBreed: startCase(sntTranslate(toLower(patient?.breed || ""))),
    patientSpecies: startCase(toLower(sntTranslate(patient?.species || ""))),
    patientBirthday: patient
      ? dateUtil(patient.birthday).format("DD/MM/YYYY")
      : "",
    patientMicrochip: patient?.microchip || "",
    patientSterilized: patientSterilized
      ? sntTranslate(marker("Si"))
      : sntTranslate(marker("No")),
    patientSex:
      patient?.sex === "male"
        ? sntTranslate(marker("Maschio"))
        : sntTranslate(marker("Femmina")),
    referringPhysician: ""
  };
}
